import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  View,
  TouchableOpacity,
  Modal,
  FlatList,
  ScrollView,
  Image,
  Share,
  Platform,
  Pressable,
  StyleSheet,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useResponsive } from '@/hooks/common/useResponsive';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

interface ImageViewerModalProps {
  visible: boolean;
  images: string[];
  initialIndex?: number;
  onClose: () => void;
}

export const ImageViewerModal: React.FC<ImageViewerModalProps> = ({
  visible,
  images,
  initialIndex = 0,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [carouselSize, setCarouselSize] = useState({ width: 0, height: 0 });
  const flatListRef = useRef<FlatList>(null);
  const { width: screenWidth, height: screenHeight, layoutMode } = useResponsive();

  const isDesktop = layoutMode === 'desktop' || layoutMode === 'tablet';
  const canGoNext = currentIndex < images.length - 1;
  const canGoPrev = currentIndex > 0;

  useEffect(() => {
    if (visible) {
      setCurrentIndex(initialIndex);
      if (initialIndex > 0) {
        setTimeout(() => {
          flatListRef.current?.scrollToIndex({ index: initialIndex, animated: false });
        }, 100);
      }
    }
  }, [visible, initialIndex]);

  const handleShare = async () => {
    try {
      const url = images[currentIndex];
      await Share.share({ url, message: url });
    } catch {
      // silently ignore
    }
  };

  const goToNext = useCallback(() => {
    if (!canGoNext) return;
    const next = currentIndex + 1;
    setCurrentIndex(next);
    flatListRef.current?.scrollToIndex({ index: next, animated: true });
  }, [currentIndex, canGoNext]);

  const goToPrev = useCallback(() => {
    if (!canGoPrev) return;
    const prev = currentIndex - 1;
    setCurrentIndex(prev);
    flatListRef.current?.scrollToIndex({ index: prev, animated: true });
  }, [currentIndex, canGoPrev]);

  useEffect(() => {
    if (!visible || Platform.OS !== 'web') return;
    const w: any = (globalThis as any)?.window;
    if (!w) return;

    const handleKeyDown = (e: any) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case 'ArrowLeft':
          goToPrev();
          break;
      }
    };

    w.addEventListener('keydown', handleKeyDown);
    return () => w.removeEventListener('keydown', handleKeyDown);
  }, [visible, onClose, goToNext, goToPrev]);

  if (images.length === 0) return null;

  // ── Desktop / Tablet: lightbox with overlay ──────────────────────────────
  if (isDesktop) {
    const imgWidth = Math.min(screenWidth * 0.7, 900);
    const imgHeight = screenHeight * 0.75;

    return (
      <Modal
        visible={visible}
        animationType="fade"
        transparent
        onRequestClose={onClose}
      >
        <Pressable style={desktopStyles.overlay} onPress={onClose}>
          <Pressable style={desktopStyles.contentWrapper} onPress={(e) => e.stopPropagation()}>
            {/* Close */}
            <TouchableOpacity
              style={desktopStyles.closeButton}
              onPress={onClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <RESIDENTS_SVG_COMPONENTS.CANCEL_X width={18} height={18} color="#fff" />
            </TouchableOpacity>

            <View style={desktopStyles.mainContent}>
              {/* Left arrow */}
              {images.length > 1 && (
                <TouchableOpacity
                  style={[desktopStyles.arrowButton, !canGoPrev && desktopStyles.arrowDisabled]}
                  onPress={goToPrev}
                  disabled={!canGoPrev}
                  activeOpacity={0.7}
                >
                  <RESIDENTS_SVG_COMPONENTS.CHEVRON_LEFT
                    width={24}
                    height={24}
                    color={canGoPrev ? '#fff' : 'rgba(255,255,255,0.3)'}
                  />
                </TouchableOpacity>
              )}

              {/* Image */}
              <View style={[desktopStyles.imageContainer, { width: imgWidth, height: imgHeight }]}>
                <FlatList
                  ref={flatListRef}
                  data={images}
                  horizontal
                  pagingEnabled
                  showsHorizontalScrollIndicator={false}
                  keyExtractor={(item, idx) => `${item}-${idx}`}
                  getItemLayout={(_, index) => ({
                    length: imgWidth,
                    offset: imgWidth * index,
                    index,
                  })}
                  onMomentumScrollEnd={(e) => {
                    const index = Math.round(e.nativeEvent.contentOffset.x / imgWidth);
                    setCurrentIndex(index);
                  }}
                  renderItem={({ item }) => (
                    <Image
                      source={{ uri: item }}
                      style={{ width: imgWidth, height: imgHeight }}
                      resizeMode="contain"
                    />
                  )}
                />

                {/* Counter badge */}
                {images.length > 1 && (
                  <View style={desktopStyles.counterBadge}>
                    <Text style={desktopStyles.counterText}>
                      {currentIndex + 1} / {images.length}
                    </Text>
                  </View>
                )}
              </View>

              {/* Right arrow */}
              {images.length > 1 && (
                <TouchableOpacity
                  style={[desktopStyles.arrowButton, !canGoNext && desktopStyles.arrowDisabled]}
                  onPress={goToNext}
                  disabled={!canGoNext}
                  activeOpacity={0.7}
                >
                  <RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT
                    width={24}
                    height={24}
                    color={canGoNext ? '#fff' : 'rgba(255,255,255,0.3)'}
                  />
                </TouchableOpacity>
              )}
            </View>

            {/* Share button */}
            {Platform.OS !== 'web' && (
              <TouchableOpacity style={desktopStyles.shareButton} onPress={handleShare} activeOpacity={0.8}>
                <Text style={desktopStyles.shareButtonText}>Share</Text>
              </TouchableOpacity>
            )}
          </Pressable>
        </Pressable>
      </Modal>
    );
  }

  // ── Mobile: full-screen swipe viewer ─────────────────────────────────────
  return (
    <Modal
      visible={visible}
      transparent={false}
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={mobileStyles.container}>
        {/* Header */}
        <View style={[mobileStyles.header, { paddingTop: Platform.OS === 'ios' ? 48 : 16 }]}>
          <TouchableOpacity onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Text style={mobileStyles.headerButton}>Close</Text>
          </TouchableOpacity>
          <Text style={mobileStyles.headerCount}>
            {currentIndex + 1} / {images.length}
          </Text>
          <TouchableOpacity onPress={handleShare} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Text style={mobileStyles.headerButton}>Share</Text>
          </TouchableOpacity>
        </View>

        {/* Image Carousel */}
        <View
          style={mobileStyles.carouselWrap}
          onLayout={(e) => {
            const { width, height } = e.nativeEvent.layout;
            setCarouselSize((prev) =>
              prev.width === width && prev.height === height ? prev : { width, height },
            );
          }}
        >
          {carouselSize.width > 0 && carouselSize.height > 0 && (
            <FlatList
              ref={flatListRef}
              data={images}
              horizontal
              pagingEnabled
              keyExtractor={(item, index) => `${item}-${index}`}
              initialScrollIndex={initialIndex}
              getItemLayout={(_, index) => ({
                length: carouselSize.width,
                offset: carouselSize.width * index,
                index,
              })}
              onMomentumScrollEnd={(e) => {
                const index = Math.round(e.nativeEvent.contentOffset.x / carouselSize.width);
                setCurrentIndex(index);
              }}
              showsHorizontalScrollIndicator={false}
              renderItem={({ item }) => (
                <ScrollView
                  style={{ width: carouselSize.width, height: carouselSize.height }}
                  contentContainerStyle={mobileStyles.imageContainer}
                  maximumZoomScale={3}
                  minimumZoomScale={1}
                  centerContent
                  showsVerticalScrollIndicator={false}
                  showsHorizontalScrollIndicator={false}
                >
                  <Image
                    source={{ uri: item }}
                    style={{
                      width: carouselSize.width,
                      height: carouselSize.height,
                      resizeMode: 'contain',
                    }}
                  />
                </ScrollView>
              )}
            />
          )}
        </View>
      </View>
    </Modal>
  );
};

// ── Desktop Styles ─────────────────────────────────────────────────────────────

const desktopStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  contentWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    maxWidth: '95%',
    maxHeight: '95%',
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  mainContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
  },
  imageContainer: {
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  arrowButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrowDisabled: {
    opacity: 0.4,
  },
  counterBadge: {
    position: 'absolute',
    top: 16,
    left: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
  },
  counterText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#fff',
  },
  shareButton: {
    marginTop: 20,
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  shareButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#fff',
  },
});

// ── Mobile Styles ──────────────────────────────────────────────────────────────

const mobileStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  header: {
    paddingHorizontal: 12,
    paddingBottom: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  carouselWrap: {
    flex: 1,
  },
  headerButton: {
    color: '#fff',
    fontSize: 17,
  },
  headerCount: {
    color: '#fff',
    fontSize: 14,
  },
  imageContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default ImageViewerModal;
